import {ElObj, elObjOpts, ElObjOpts} from '../elobj';
import {OBJ, Obj, SIGNAL, SLOT} from '../obj';
import {bind, clamp} from '../util';
import {Point, list} from '../tools';
import {Menu} from './menu';

export interface SelectMenuItem {
	disabled: boolean;
	secondaryText: string;
	text: string;
	value: string;
}

@OBJ
export class SelectMenu extends ElObj {
	private static activeOwner: SelectMenu | null = null;
	private static readonly menuHorizontalPadding = 72;
	private static readonly menuSecondaryLineGap = 6;
	private static readonly menuTrailingIconWidth = 32;
	private static readonly primaryFont = '600 13px Roboto, Arial, sans-serif';
	private static readonly secondaryFont = '400 11px Roboto, Arial, sans-serif';
	private currentIndexValue: number;
	private disabled: boolean;
	private items: list<SelectMenuItem>;
	private labelEl: ElObj;
	private menu: Menu | null;
	private trigger: ElObj;
	private triggerIconEl: ElObj;

	constructor(opts: Partial<ElObjOpts> | null, tagName: TagName, parent?: ElObj | null);
	constructor(opts: Partial<ElObjOpts> | null, root: Element | null, parent?: ElObj | null);
	constructor(tagName: TagName, parent?: ElObj | null);
	constructor(root: Element | null, parent?: ElObj | null);
	constructor(opts: Partial<ElObjOpts> | null, tagName?: TagName);
	constructor(opts: Partial<ElObjOpts> | null, root?: Element | null);
	constructor(opts: Partial<ElObjOpts>, parent?: ElObj | null);
	constructor(opts?: Partial<ElObjOpts>);
	constructor(root?: Element | null);
	constructor(tagName?: TagName);
	constructor(parent?: ElObj | null);
	constructor(a?: Partial<ElObjOpts> | ElObj | Element | TagName | null, b?: ElObj | Element | TagName | null, c?: ElObj | null) {
		const opts = elObjOpts<ElObjOpts>(a, b, c);
		const classNames = opts.classNames || [];
		opts.classNames = ['lb-select-menu', ...classNames];
		opts.tagName = 'div';
		super(opts);
		this.currentIndexValue = -1;
		this.disabled = false;
		this.items = new list<SelectMenuItem>();
		this.menu = null;
		this.trigger = new ElObj({
			attributes: [
				['type', 'button'],
				['aria-expanded', 'false'],
				['aria-haspopup', 'listbox'],
			],
			classNames: ['lb-select-menu__trigger'],
			parent: this,
			tagName: 'button',
		});
		this.labelEl = new ElObj({
			classNames: ['lb-select-menu__label'],
			parent: this.trigger,
			tagName: 'span',
		});
		this.triggerIconEl = new ElObj({
			classNames: ['material-icons', 'lb-select-menu__icon'],
			parent: this.trigger,
			tagName: 'span',
		});
		this.triggerIconEl.setAttribute('aria-hidden', 'true');
		this.triggerIconEl.setText('expand_more');
		this.updateTriggerLabel();
		this.addEventListeners();
	}

	private addEventListeners(): void {
		this.trigger.addEventListener('click', this.domEvent);
		this.trigger.addEventListener('keydown', this.domEvent);
	}

	addItem(item: string | Partial<SelectMenuItem>, value?: string): void {
		let payload: SelectMenuItem;
		if (typeof item === 'string') {
			payload = {
				disabled: false,
				secondaryText: '',
				text: item,
				value: value === undefined ? item : value,
			};
		} else {
			payload = {
				disabled: Boolean(item.disabled),
				secondaryText: item.secondaryText || '',
				text: item.text || '',
				value: item.value === undefined ? String(item.text || '') : item.value,
			};
		}
		this.items.append(payload);
		if ((this.currentIndexValue < 0) && !payload.disabled) {
			this.currentIndexValue = this.items.count() - 1;
		}
		this.updateTriggerLabel();
	}

	clear(): void {
		this.destroyMenu();
		this.items.clear();
		this.currentIndexValue = -1;
		this.updateTriggerLabel();
	}

	closeMenu(): void {
		if (this.menu) {
			this.menu.close();
		}
	}

	count(): number {
		return this.items.count();
	}

	currentIndex(): number {
		return this.currentIndexValue;
	}

	currentText(): string {
		const item = this.item(this.currentIndexValue);
		return item ? item.text : '';
	}

	@SIGNAL
	currentIndexChanged(index: number): void {
	}

	@SIGNAL
	currentTextChanged(text: string): void {
	}

	currentValue(): string {
		const item = this.item(this.currentIndexValue);
		return item ? item.value : '';
	}

	destroy(): void {
		if (SelectMenu.activeOwner === this) {
			SelectMenu.activeOwner = null;
		}
		this.destroyMenu();
		this.removeEventListeners();
		this.triggerIconEl.destroy();
		this.labelEl.destroy();
		this.trigger.destroy();
		super.destroy();
	}

	private destroyMenu(): void {
		if (this.menu) {
			Obj.disconnect(this.menu, 'closed', this, 'menuClosed');
			Obj.disconnect(this.menu, 'selectionChanged', this, 'menuSelectionChanged');
			this.menu.destroy();
		}
		this.menu = null;
		this.removeClass('lb-select-menu--open');
		this.trigger.setAttribute('aria-expanded', 'false');
	}

	@bind
	private domEvent(event: Event): void {
		switch (event.type) {
			case 'click':
				event.preventDefault();
				event.stopImmediatePropagation();
				event.stopPropagation();
				if (this.menu) {
					this.closeMenu();
				} else {
					this.openMenu();
				}
				break;
			case 'keydown': {
				const keyEvent = <KeyboardEvent>event;
				if ((keyEvent.key === 'ArrowDown') || (keyEvent.key === 'Enter') || (keyEvent.key === ' ')) {
					keyEvent.preventDefault();
					keyEvent.stopImmediatePropagation();
					keyEvent.stopPropagation();
					this.openMenu();
				}
				break;
			}
		}
	}

	hasAcceptableInput(): boolean {
		return this.currentIndexValue >= 0;
	}

	item(index: number): SelectMenuItem | null {
		if ((index < 0) || (index >= this.items.count())) {
			return null;
		}
		return this.items.at(index) || null;
	}

	itemText(index: number): string {
		const item = this.item(index);
		return item ? item.text : '';
	}

	private openMenu(): void {
		if (this.disabled || !this.items.count() || this.menu) {
			return;
		}
		if (SelectMenu.activeOwner && (SelectMenu.activeOwner !== this)) {
			SelectMenu.activeOwner.closeMenu();
		}
		const triggerEl = this.trigger.element();
		if (!triggerEl) {
			return;
		}
		const rect = triggerEl.getBoundingClientRect();
		this.menu = new Menu({classNames: ['lb-select-menu__menu']});
		this.menu.setStyleProperty('min-width', `${this.menuMinWidth(rect.width)}px`);
		Obj.connect(this.menu, 'closed', this, 'menuClosed');
		Obj.connect(this.menu, 'selectionChanged', this, 'menuSelectionChanged');
		for (let index = 0; index < this.items.count(); ++index) {
			const item = this.items.at(index);
			this.menu.addItem({
				classNames: ['lb-select-menu__menu-item'],
				secondaryText: item.secondaryText,
				text: item.text,
				trailingIcon: index === this.currentIndexValue ? {name: 'done'} : undefined,
				twoLine: item.secondaryText.length > 0,
			});
			const menuItem = this.menu.item(index);
			if (menuItem) {
				menuItem.setDisabled(item.disabled);
				if (index === this.currentIndexValue) {
					menuItem.addClass('lb-select-menu__menu-item--selected');
				}
			}
		}
		this.addClass('lb-select-menu--open');
		this.trigger.setAttribute('aria-expanded', 'true');
		SelectMenu.activeOwner = this;
		this.menu.open(new Point(rect.left, rect.bottom + 4));
	}

	private measureTextWidth(text: string, font: string): number {
		if (!text.length) {
			return 0;
		}
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return text.length * 8;
		}
		ctx.font = font;
		return ctx.measureText(text).width;
	}

	private menuMinWidth(triggerWidth: number): number {
		let width = Math.ceil(triggerWidth);
		for (let index = 0; index < this.items.count(); ++index) {
			const item = this.items.at(index);
			const primaryWidth = this.measureTextWidth(item.text, SelectMenu.primaryFont);
			const secondaryWidth = this.measureTextWidth(item.secondaryText, SelectMenu.secondaryFont);
			const itemWidth = Math.max(primaryWidth, secondaryWidth)
				+ SelectMenu.menuHorizontalPadding
				+ SelectMenu.menuTrailingIconWidth
				+ (item.secondaryText ? SelectMenu.menuSecondaryLineGap : 0);
			width = Math.max(width, Math.ceil(itemWidth));
		}
		const maxWidth = Math.max(160, Math.floor(window.innerWidth - 32));
		return Math.min(width, maxWidth);
	}

	@SLOT
	private menuClosed(): void {
		this.destroyMenu();
		if (SelectMenu.activeOwner === this) {
			SelectMenu.activeOwner = null;
		}
	}

	@SLOT
	private menuSelectionChanged(index: number): void {
		this.setCurrentIndex(index);
	}

	private removeEventListeners(): void {
		this.trigger.removeEventListener('click', this.domEvent);
		this.trigger.removeEventListener('keydown', this.domEvent);
	}

	setAttribute(name: string, value: string): void;
	setAttribute(attrs: Iterable<[string, string]>): void;
	setAttribute(a: Iterable<[string, string]> | string, b?: string): void {
		super.setAttribute(<any>a, <any>b);
		if (typeof a === 'string') {
			if (a === 'title') {
				this.trigger.setAttribute(a, <string>b);
			}
			if (a === 'aria-label') {
				this.trigger.setAttribute(a, <string>b);
			}
		}
	}

	setCurrentIndex(index: number, allowDisabled: boolean = false, blockSignals: boolean = false): void {
		if (!this.items.count()) {
			this.currentIndexValue = -1;
			this.updateTriggerLabel();
			return;
		}
		index = clamp(0, index, this.items.count() - 1);
		const item = this.items.at(index);
		if (item.disabled && !allowDisabled) {
			return;
		}
		if (index === this.currentIndexValue) {
			this.updateTriggerLabel();
			return;
		}
		this.currentIndexValue = index;
		this.updateTriggerLabel();
		if (blockSignals) {
			return;
		}
		this.currentIndexChanged(this.currentIndexValue);
		this.currentTextChanged(this.currentText());
	}

	setDisabled(disabled: boolean): void {
		this.disabled = disabled;
		this.trigger.setDisabled(disabled);
		this.setClass(disabled, 'lb-select-menu--disabled');
		if (disabled) {
			this.closeMenu();
		}
	}

	private updateTriggerLabel(): void {
		const item = this.item(this.currentIndexValue);
		this.labelEl.setText(item ? item.text : '—');
		this.trigger.setClass(Boolean(item && item.secondaryText), 'lb-select-menu__trigger--two-line');
	}
}
